import dayjs from 'dayjs';
import React, { useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon } from 'tdesign-icons-react';
import { Button, DateRangePicker, Dropdown, Form, Radio, Space } from 'tdesign-react';
import { isEmpty } from 'lodash';
import { produce } from 'immer';
import SvgIcon from '@/components/SvgIcon';
import Filter from './Filter';

const { FormItem } = Form;

const _options = [
  {
    content: '5分钟',
    value: '5min',
  },
  {
    content: '3小时',
    value: '3h',
  },
  {
    content: '6小时',
    value: '6h',
  },
  {
    content: '2天',
    value: '2d',
  },
  {
    content: '7天',
    value: '7d',
  },
  {
    content: '30天',
    value: '30d',
  },
];

function SearchBar({ onChange, onRefresh, properties, changeProperties, loading }, ref) {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [currentTime, setCurrentTime] = useState();

  useImperativeHandle(ref, () => ({
    rangeChange: (range) => {
      form.setFields([{ name: 'range', value: range }]);
      onRangeChange(null, { dayjsValue: range });
    },
    reset: (initialData) => {
      form.setFieldsValue(initialData);
      setCurrentTime(initialData.time);
    },
  }));

  const options = useMemo(
    () =>
      produce(_options, (draft) => {
        // 选中的快捷选项
        const currentSelected = draft.find((item) => item.value === currentTime);
        if (currentSelected) {
          currentSelected.active = true;
        }
      }),
    [currentTime],
  );

  const onRefreshClick = () => {
    if (currentTime) {
      onTimeChange(currentTime);
    } else {
      onRefresh();
    }
  };

  const onTimeChange = (value) => {
    setCurrentTime(value);
    const now = dayjs().startOf('minute').add(1, 'minute');
    let from;
    if (value === '5min') {
      from = now.subtract(5, 'minute');
    }
    if (value === '15min') {
      from = now.subtract(15, 'minute');
    }
    if (value === '1h') {
      from = now.subtract(1, 'hour');
    }
    if (value === '3h') {
      from = now.subtract(3, 'hour');
    }
    if (value === '6h') {
      from = now.subtract(6, 'hour');
    }
    if (value === '24h') {
      from = now.subtract(24, 'hour');
    }
    if (value === '2d') {
      from = now.subtract(2, 'day');
    }
    if (value === '7d') {
      from = now.subtract(7, 'day');
    }
    if (value === '30d') {
      from = now.subtract(30, 'day');
    }
    const range = [from.toDate(), now.toDate()];
    form.setFieldsValue({
      range,
    });
    onChange(range);
  };

  const onRangeChange = (_, { dayjsValue }) => {
    if (isEmpty(dayjsValue)) {
      return;
    }
    form.setFieldsValue({ time: 0 }); // 取消快捷选择
    setCurrentTime(null);
    const [from, to] = dayjsValue;
    const range = [from.toDate(), to.toDate()];
    onChange(range);
  };

  const clickHandler = (data) => {
    form.setFieldsValue({ time: 0 }); // 取消快捷选择
    onTimeChange(data.value);
  };

  return (
    <Root>
      <Form form={form} layout='inline'>
        <FormItem name='time' className='hack_radio_button_theme'>
          <Radio.Group onChange={onTimeChange} variant='primary-filled'>
            <Radio.Button value={'15min'}>15分钟</Radio.Button>
            <Radio.Button value={'1h'}>1小时</Radio.Button>
            <Radio.Button value={'24h'}>24小时</Radio.Button>
          </Radio.Group>
        </FormItem>
        <FormItem name='range'>
          <DateRangePicker onChange={onRangeChange} enableTimePicker allowInput clearable />
        </FormItem>
        <Dropdown options={options} onClick={clickHandler}>
          <Button variant='text' suffix={<ChevronDownIcon size={16} />}>
            更多
          </Button>
        </Dropdown>
      </Form>
      <Space size='small'>
        <Button
          variant='outline'
          onClick={onRefreshClick}
          icon={<Icon type='refresh' className={loading ? 't-icon-loading' : null} />}
        />
        <Button onClick={() => setVisible(true)} variant='outline' icon={<Icon type='filter' />} />
      </Space>
      <Filter
        properties={properties}
        changeProperties={changeProperties}
        visible={visible}
        handleClose={() => setVisible(false)}
      />
    </Root>
  );
}

export default React.forwardRef(SearchBar);

const Root = styled.div`
  margin: 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .t-button {
    padding: 0 8px;
  }
`;

const Icon = styled(SvgIcon)`
  font-size: 16px;
  color: var(--td-font-gray-2);
`;
